import { fetchApi } from './api';
import type {
    BankBalanceEntry,
    BankAccount,
    BankAccountConfig,
    CurrencySummary,
    BankComparison,
    TrendData,
    SyncCheckResult,
    CreateEntryData,
    UpdateEntryData,
    CopyEntriesData,
    CreateBankConfigData,
    UpdateBankConfigData,
    WeeklyBalances,
    SaveWeeklyData,
} from '../types/bankBalance';

export const bankBalanceApi = {
    // Balance Entries
    listEntries: (year: number, month: number, companyCode?: string) => {
        const params = new URLSearchParams({ year: String(year), month: String(month) });
        if (companyCode) params.set('companyCode', companyCode);
        return fetchApi<{ entries: BankBalanceEntry[]; companyCode: string }>(`/bank-balance?${params}`);
    },

    getBankAccounts: (companyCode?: string) => {
        const params = companyCode ? `?companyCode=${companyCode}` : '';
        return fetchApi<BankAccount[]>(`/bank-balance/bank-accounts${params}`);
    },

    createEntry: (data: CreateEntryData) =>
        fetchApi<{ entryId: number; createdAt: string }>('/bank-balance', {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    updateEntry: (id: number, data: UpdateEntryData) =>
        fetchApi<{ entryId: number; rowsAffected: number }>(`/bank-balance/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),

    deleteEntry: (id: number) =>
        fetchApi<{ entryId: number; deleted: boolean }>(`/bank-balance/${id}`, {
            method: 'DELETE',
        }),

    copyEntries: (data: CopyEntriesData) =>
        fetchApi<{ copiedCount: number; skippedCount: number }>('/bank-balance/copy', {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    // Quick entry (/bank-balance/weekly)
    getWeekly: (date?: string, companyCode?: string) => {
        const params = new URLSearchParams();
        if (date) params.set('date', date);
        if (companyCode) params.set('companyCode', companyCode);
        const qs = params.toString();
        return fetchApi<WeeklyBalances>(`/bank-balance/weekly${qs ? `?${qs}` : ''}`);
    },

    saveWeekly: (data: SaveWeeklyData) =>
        fetchApi<{ companyCode: string; postingDate: string; inserted: number; updated: number; unchanged: number }>(
            '/bank-balance/weekly', { method: 'POST', body: JSON.stringify(data) }),

    getSummary: (year: number, month: number, companyCode?: string) => {
        const params = new URLSearchParams({ year: String(year), month: String(month) });
        if (companyCode) params.set('companyCode', companyCode);
        return fetchApi<CurrencySummary[]>(`/bank-balance/summary?${params}`);
    },

    getComparison: (year: number, month: number, companyCode?: string) => {
        const params = new URLSearchParams({ year: String(year), month: String(month) });
        if (companyCode) params.set('companyCode', companyCode);
        return fetchApi<BankComparison[]>(`/bank-balance/comparison?${params}`);
    },

    getTrend: (companyCode?: string) => {
        const params = companyCode ? `?companyCode=${companyCode}` : '';
        return fetchApi<TrendData>(`/bank-balance/trend${params}`);
    },

    // Bank Account Config
    listConfig: (companyCode?: string) => {
        const params = companyCode ? `?companyCode=${companyCode}` : '';
        return fetchApi<BankAccountConfig[]>(`/bank-balance/config${params}`);
    },

    syncCheck: () =>
        fetchApi<SyncCheckResult>('/bank-balance/config/sync-check'),

    createConfig: (data: CreateBankConfigData) =>
        fetchApi<{ id: number; createdAt: string }>('/bank-balance/config', {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    updateConfig: (bankId: string, data: UpdateBankConfigData) =>
        fetchApi<{ bankId: string; updated: boolean }>(`/bank-balance/config/${encodeURIComponent(bankId)}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),

    deleteConfig: (bankId: string) =>
        fetchApi<{ bankId: string; deactivated: boolean }>(`/bank-balance/config/${encodeURIComponent(bankId)}`, {
            method: 'DELETE',
        }),
};
